<template>
  <div class="course-card">
    <img :src="course.image" alt="课程封面" class="course-image" />

    <div class="course-info">
      <div class="course-header">
        <h3 class="course-title">{{ course.name }}</h3>
        <span class="course-category">{{ course.category }}</span>
      </div>

      <div class="course-meta">
        <span class="meta-item">{{ course.score }}积分</span>
        <span class="meta-separator">|</span>
        <span class="meta-item">{{ course.duration }}分钟</span>
        <span class="meta-separator">|</span>
        <span class="meta-item">{{ course.credits }}学分</span>
      </div>

      <p class="course-description">{{ course.description }}</p>

      <div class="course-footer">
        <span class="assigned-department"
          >已指派: {{ formattedAssignedPerson }}</span
        >
        <span class="study-status"
          >状态: <span class="status-text">{{ course.videoState }}</span></span
        >
        <button class="play-button" @click="openVideoFullscreen">
          <i class="fas fa-play-circle"></i> 播放视频
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CourseCard",
  props: {
    id: {
      type: Number,
      required: true,
      default: null,
    },
    course: {
      type: Object,
      required: true,
      validator: (course) => {
        const requiredFields = [
          "name",
          "category",
          "score",
          "duration",
          "credits",
          "description",
          "assigned",
          "image",
          "videoUrl",
          "videoState",
        ];
        return requiredFields.every((field) => field in course);
      },
    },
  },
  computed: {
    formattedAssignedPerson() {
      // 如果没有指派信息，返回空字符串
      if (!this.course.assigned) return "";

      // 检查是否包含多个人员（通过逗号分隔判断）
      const hasMultiplePersons = this.course.assigned.includes(",");

      if (hasMultiplePersons) {
        // 获取第一个人员的姓名（假设用逗号分隔）
        const firstPerson = this.course.assigned.split(",")[0].trim();
        // 移除可能的"部门:"前缀
        const cleanName = firstPerson.replace(/^部门:/, "").trim();
        return cleanName + "...";
      } else {
        // 只有一个人员，直接返回，但也要移除可能的"部门:"前缀
        return this.course.assigned.replace(/^部门:/, "").trim();
      }
    },
  },
  data() {
    return {
      videoElement: null,
      videoStartTime: 0,
      progressUpdateFlag: false,
      updateStatus: "",
      updateMessage: "",
      studyStatus: "未开始",
      lastPlayTime: 0, // 用于检测快进的变量
      fastForwardWarningShown: false, // 用于防止重复显示警告的变量
    };
  },
  methods: {
    // loadStudyProgress() {
    //   const savedProgress = localStorage.getItem(`course_${this.id}_progress`)
    //   if (savedProgress) {
    //     this.studyProgress = parseInt(savedProgress)
    //     this.studyStatus = this.computedStudyStatus
    //   }
    // },
    openVideoFullscreen() {
      const fullscreenContainer = document.createElement("div");
      fullscreenContainer.className = "video-fullscreen-overlay";

      // 创建视频元素
      this.videoElement = document.createElement("video");

      // 修复混合内容错误：将HTTP视频URL转换为HTTPS
      let videoUrl = this.course.videoUrl;
      if (videoUrl && videoUrl.startsWith("http://")) {
        videoUrl = videoUrl.replace("http://", "https://");
        console.log("已将视频URL从HTTP转换为HTTPS:", videoUrl);
      }

      this.videoElement.src = videoUrl;
      this.videoElement.controls = true;
      this.videoElement.autoplay = true;
      this.videoElement.className = "fullscreen-video";

      // 绑定事件监听
      this.bindVideoEvents();

      // 创建关闭按钮
      const closeButton = this.createCloseButton();

      // 组装组件
      fullscreenContainer.appendChild(closeButton);
      fullscreenContainer.appendChild(this.videoElement);
      document.body.appendChild(fullscreenContainer);

      // 添加样式
      this.addFullscreenStyles();

      // 尝试自动播放
      this.attemptAutoPlay();
    },

    bindVideoEvents() {
      this.videoElement.addEventListener(
        "loadedmetadata",
        this.handleVideoMetadata
      );
      this.videoElement.addEventListener("play", this.handleVideoPlay);
      this.videoElement.addEventListener("ended", this.handleVideoEnded);
      this.videoElement.addEventListener("timeupdate", this.handleTimeUpdate);
    },

    createCloseButton() {
      const closeButton = document.createElement("button");
      closeButton.className = "close-fullscreen-btn";
      closeButton.innerHTML = '<i class="fas fa-times"></i> 退出';
      closeButton.onclick = this.closeFullscreen;
      return closeButton;
    },

    addFullscreenStyles() {
      const style = document.createElement("style");
      style.textContent = this.getFullscreenStyles();
      document.head.appendChild(style);
    },

    getFullscreenStyles() {
      return `
        .video-fullscreen-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: rgba(0, 0, 0, 0.95);
          z-index: 9999;
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .fullscreen-video {
          width: 90vw;
          height: 90vh;
          object-fit: contain;
        }
        .close-fullscreen-btn {
          position: absolute;
          top: 20px;
          right: 20px;
          background: rgba(255, 255, 255, 0.2);
          border: none;
          color: white;
          width: 80px;
          height: 40px;
          border-radius: 20px;
          cursor: pointer;
          z-index: 10000;
          font-size: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 5px;
        }
        .close-fullscreen-btn:hover {
          background: rgba(255, 255, 255, 0.3);
        }
        .video-info-panel {
          position: absolute;
          bottom: 20px;
          left: 20px;
          background: rgba(255, 255, 255, 0.1);
          color: white;
          padding: 10px 15px;
          border-radius: 8px;
          font-size: 14px;
          backdrop-filter: blur(10px);
        }
      `;
    },

    attemptAutoPlay() {
      this.$nextTick(() => {
        const videoElement = document.querySelector(".fullscreen-video");
        if (videoElement) {
          videoElement.play().catch(() => {
            console.log("自动播放被阻止，需要用户交互");
            videoElement.controls = true;
          });
        }
      });
    },

    handleVideoMetadata() {
      if (this.videoElement) {
        const totalDuration = this.videoElement.duration;
        console.log("视频总时长:", totalDuration.toFixed(2), "秒");
      }
    },

    handleVideoPlay() {
      if (!this.videoElement) return;

      const currentTime = this.videoElement.currentTime;
      const totalDuration = this.videoElement.duration;
      this.videoStartTime = Date.now();
      this.lastPlayTime = currentTime; // 初始化播放时间
      this.fastForwardWarningShown = false; // 重置警告显示标志

      console.log("视频开始播放");
      console.log("当前播放时间:", currentTime.toFixed(2), "秒");
      console.log("视频总时长:", totalDuration.toFixed(2), "秒");

      this.$emit("video-play", {
        courseName: this.course.name,
        currentTime: currentTime,
        totalDuration: totalDuration,
        playPosition: ((currentTime / totalDuration) * 100).toFixed(2) + "%",
        timestamp: new Date(),
      });
    },

    /*今天新的调试代码*/
    // 视频播放结束处理
    async handleVideoEnded() {
      console.log("视频播放结束，开始更新状态，课程ID:", this.id);
      // 更新状态为加载中
      this.updateStatus = "loading";
      this.updateMessage = "正在更新播放状态...";

      try {
        // 计算实际播放时间（排除暂停、跳转等）
        if (!this.videoElement) return;
        const totalDuration = this.videoElement.duration;
        const actualPlayTime = (Date.now() - this.videoStartTime) / 1000;

        // 调试：检查所有参数值
        console.group("🔍 参数值检查");
        console.log("videoId (prop):", this.id);
        console.log("videoState:", "已完成");
        console.log("totalDuration:", totalDuration);
        console.log("actualPlayTime:", actualPlayTime);
        console.log("completionDate:", new Date().toISOString().split("T")[0]);
        console.groupEnd();

        // 确保id有值
        const recordId = this.id;
        if (!recordId || recordId === "undefined") {
          throw new Error("视频ID不能为空或undefined");
        }
        // 调用后端接口更新视频状态
        const response = await this.updateVideoStatus({
          id: recordId,
          videoState: "已完成",
          totalPlayedTime: totalDuration,
          actualPlayTime: actualPlayTime,
        });
        if (response && response.id) {
          this.updateStatus = "success";
          this.updateMessage = "视频学习完成！状态已更新。";
          console.log("✅ 视频状态更新成功:", response);

          // 通知父组件视频已完成
          this.$emit("videoCompleted", {
            id: recordId,
            completionDate: new Date().toISOString().split("T")[0],
            totalPlayedTime: this.totalPlayedTime,
            actualPlayTime: actualPlayTime,
          });
        } else {
          throw new Error("更新失败：未收到有效响应");
        }
      } catch (error) {
        console.error("更新视频状态失败:", error);
        this.updateStatus = "error";
        this.updateMessage = `更新失败: ${error.message || "请稍后重试"}`;

        // 通知父组件更新失败
        this.$emit("videoUpdateError", {
          id: this.videoId,
          error: error.message,
        });
      }
    },
    // 调用后端API更新视频状态
    async updateVideoStatus(params) {
      const { id, videoState, totalPlayedTime, actualPlayTime } = params;

      // 构建查询参数 - 修正参数名与后端匹配
      const queryParams = {
        videoState: videoState,
        totalPlayedTime: String(Math.round(totalPlayedTime)),
        actualPlayTime: String(Math.round(actualPlayTime)),
      };

      // 重要：根据服务端错误日志，客户端提供的日期字符串无法被正确转换为LocalDate
      // 解决方案：完全不传递completionDate参数，让服务端自己处理日期逻辑
      // 移除所有关于completionDate的处理，避免类型转换错误

      // 使用this.$http发送PUT请求（根据main.js中的配置，baseURL已设置为'/api'）
      const response = await this.$http.put(
        `/training/${id}/video-status`,
        null,
        {
          params: queryParams,
        }
      );

      console.log("更新请求响应:", response);
      return response.data;
    },

    handleTimeUpdate() {
      if (!this.videoElement) return;

      const currentTime = this.videoElement.currentTime;
      const totalDuration = this.videoElement.duration;

      // 实时更新播放进度
      const progress = (currentTime / totalDuration) * 100;
      console.log("当前播放进度:", progress.toFixed(1) + "%");

      // 检测快进操作
      if (this.lastPlayTime > 0) {
        // 移除playbackRate检查，确保快进检测更灵敏
        const timeDiff = currentTime - this.lastPlayTime;
        // 计算正常应该经过的时间
        const expectedTimeDiff = 2.0; // 允许2秒的误差，更容易触发警告

        console.log(
          "播放时间差:",
          timeDiff.toFixed(2),
          "秒，预期:",
          expectedTimeDiff,
          "秒"
        );
        if (timeDiff > expectedTimeDiff) {
          console.log("检测到快进操作：", timeDiff.toFixed(2), "秒");
          this.showFastForwardWarning();
        }
      }

      // 更新最后播放时间
      this.lastPlayTime = currentTime;

      // 当进度达到2%且未更新过状态时
      if (progress >= 2 && !this.progressUpdateFlag) {
        this.updateVideoProgress();
      }
    },
    // 显示快进警告
    showFastForwardWarning() {
      // 防止重复显示警告
      if (!this.fastForwardWarningShown) {
        window.console.log("使用$message.warning显示警告");
        try {
          alert("快进阅览课程，影响你的成绩");
        } catch (error) {
          console.error("$message.warning调用失败:", error);
          alert("快进阅览课程，影响你的成绩");
        }

        this.fastForwardWarningShown = true;

        // 3秒后允许再次显示警告
        setTimeout(() => {
          this.fastForwardWarningShown = false;
        }, 3000);
      } else {
        console.log("警告已显示，防止重复");
      }
    },
    async updateVideoProgress() {
      console.log("检测到进度达到10%，开始更新状态...");

      try {
        const totalDuration = this.videoElement.duration;
        const response = await this.updateVideoStatus({
          id: this.id,
          videoState: "进行中",
          totalPlayedTime: totalDuration,
          actualPlayTime: (Date.now() - this.videoStartTime) / 1000,
        });

        if (response && response.id) {
          this.progressUpdateFlag = true;
          this.updateStatus = "success";
          this.updateMessage = "学习状态已更新为进行中";
        } else {
          throw new Error("进度状态更新失败");
        }
      } catch (error) {
        this.updateStatus = "error";
        this.updateMessage = `进度更新失败: ${error.message}`;
      }
    },

    closeFullscreen() {
      // 移除事件监听
      if (this.videoElement) {
        this.videoElement.removeEventListener(
          "loadedmetadata",
          this.handleVideoMetadata
        );
        this.videoElement.removeEventListener("play", this.handleVideoPlay);
        this.videoElement.removeEventListener("ended", this.handleVideoEnded);
        this.videoElement.removeEventListener(
          "timeupdate",
          this.handleTimeUpdate
        );
      }

      const overlay = document.querySelector(".video-fullscreen-overlay");
      if (overlay) {
        overlay.remove();
      }
    },
  },
};
</script>

<style scoped>
.course-card {
  width: 280px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  background: white;
  transition: transform 0.3s;
}

.course-card:hover {
  transform: translateY(-5px);
}

.course-image {
  width: 100%;
  height: 160px;
  object-fit: cover;
  display: block;
}

.course-info {
  padding: 16px;
}

.course-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
}

.course-title {
  font-size: 16px;
  margin: 0;
  color: #333;
  font-weight: 600;
}

.course-category {
  font-size: 12px;
  padding: 2px 8px;
  background: #e1f5fe;
  color: #0288d1;
  border-radius: 4px;
}

.course-meta {
  display: flex;
  font-size: 12px;
  color: #666;
  margin-bottom: 12px;
}

.meta-separator {
  margin: 0 4px;
  color: #ddd;
}

.course-description {
  font-size: 13px;
  color: #555;
  line-height: 1.5;
  margin-bottom: 16px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.course-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.assigned-department {
  font-size: 12px;
  color: #666;
}
.study-status {
  color: #666;
  font-size: 12px;
  margin-left: 13px;
}

.status-text {
  color: #666;
  font-weight: 500;
}
.play-button {
  background: #1890ff;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: background 0.3s;
}

.play-button:hover {
  background: #40a9ff;
}
</style>
